import moment from "moment-timezone";
import "moment/locale/fr";
import "moment/locale/ar";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../../api/api";
import EvapoChart from "./EvapoChart";

const DAY_MS = 24 * 60 * 60 * 1000;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const mondayFor = (value = new Date()) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return date;
};

const shiftDate = (date, days) => new Date(date.getTime() + days * DAY_MS);
const isNoEtcError = (code) =>
  ["no_kc_for_selected_week", "no_data_for_selected_week"].includes(code);

const HourlyEvapoChart = ({ fieldUid, fallbackData = [] }) => {
  const { t, i18n } = useTranslation();
  const currentMonday = useMemo(() => mondayFor(), []);
  const [displayMode, setDisplayMode] = useState("daily");
  const [weekStart, setWeekStart] = useState(currentMonday);
  const [hourlyData, setHourlyData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noData, setNoData] = useState(false);
  const selectedFromCalendar = useRef(false);

  const weekEnd = useMemo(() => shiftDate(weekStart, 6), [weekStart]);
  const selectedRange = useMemo(
    () =>
      `${moment(weekStart).locale(i18n.language).format("DD/MM/YYYY")} – ${moment(
        weekEnd,
      )
        .locale(i18n.language)
        .format("DD/MM/YYYY")}`,
    [i18n.language, weekEnd, weekStart],
  );

  const showNoDataModal = (range) => {
    Swal.fire({
      icon: "info",
      title: t("etc_no_data_title", {
        defaultValue: "Aucune donnée ETc disponible",
      }),
      text: t("etc_no_data_selected_period", {
        range,
        defaultValue:
          "La période {{range}} ne contient aucune donnée d'évapotranspiration (ETc). Choisissez une autre date.",
      }),
      confirmButtonText: t("close", { defaultValue: "Fermer" }),
      confirmButtonColor: "#159bad",
    });
  };

  useEffect(() => {
    let active = true;
    const calendarSelection = selectedFromCalendar.current;
    selectedFromCalendar.current = false;

    const load = async () => {
      if (!fieldUid) return;
      setLoading(true);
      setError("");
      setNoData(false);
      try {
        const response = await api.get(
          `/fields/${fieldUid}/evapotranspiration/hourly`,
          { params: { week_start: formatDateInput(weekStart) } },
        );
        if (!active) return;

        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        const hasEtcValues = rows.some(
          (item) => item.etc !== null && item.etc !== undefined && item.etc !== "",
        );
        setHourlyData(rows);
        setMeta(response.data?.meta || null);
        setNoData(!hasEtcValues);
        if (calendarSelection && !hasEtcValues) showNoDataModal(selectedRange);
      } catch (requestError) {
        if (!active) return;
        const errorCode = requestError.response?.data?.message;
        setHourlyData([]);
        setMeta(null);

        if (isNoEtcError(errorCode)) {
          setNoData(true);
          if (calendarSelection) showNoDataModal(selectedRange);
          return;
        }

        const errorDefaults = {
          field_coordinates_are_required:
            "Les coordonnées géographiques du champ sont obligatoires.",
          hourly_weather_data_unavailable:
            "Les données météo horaires sont temporairement indisponibles.",
        };
        setError(
          errorCode
            ? t(errorCode, { defaultValue: errorDefaults[errorCode] || errorCode })
            : t("hourly_evapotranspiration_error", {
                defaultValue: "Impossible de charger les données ETc.",
              }),
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [fieldUid, selectedRange, t, weekStart]);

  const dailyData = useMemo(() => {
    const totals = new Map();
    hourlyData.forEach((item) => {
      const date = item.date || String(item.timestamp || "").slice(0, 10);
      if (!date) return;
      const current = totals.get(date) || { ETC: 0, dates: date, hasEtc: false };
      if (item.etc !== null && item.etc !== undefined && item.etc !== "") {
        current.ETC += Number(item.etc) || 0;
        current.hasEtc = true;
      }
      totals.set(date, current);
    });
    return [...totals.values()]
      .filter((item) => item.hasEtc)
      .sort((a, b) => a.dates.localeCompare(b.dates));
  }, [hourlyData]);

  const chart = useMemo(
    () => ({
      labels: hourlyData.map((item) => item.timestamp),
      datasets: [
        {
          label: t("hourly_etc_mm", { defaultValue: "ETc horaire (mm)" }),
          data: hourlyData.map((item) =>
            item.etc === null || item.etc === undefined ? null : Number(item.etc),
          ),
          fill: true,
          backgroundColor: "rgba(39, 166, 183, 0.12)",
          borderColor: "#26a6b7",
          borderWidth: 2,
          pointRadius: 1.5,
          pointHoverRadius: 5,
          tension: 0.25,
          spanGaps: false,
        },
      ],
    }),
    [hourlyData, t],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: true, position: "bottom" },
        tooltip: {
          callbacks: {
            title: (items) => {
              const item = hourlyData[items[0]?.dataIndex];
              return item
                ? moment(item.timestamp)
                    .tz(meta?.timezone || "UTC")
                    .locale(i18n.language)
                    .format("dddd DD/MM/YYYY HH:mm z")
                : "";
            },
            afterBody: (items) => {
              const item = hourlyData[items[0]?.dataIndex];
              if (!item) return [];
              return [
                `ET0: ${Number(item.et0 || 0).toFixed(3)} mm`,
                `Kc: ${item.kc === null ? "—" : Number(item.kc).toFixed(3)}`,
                item.is_forecast
                  ? t("forecast_value", { defaultValue: "Valeur prévisionnelle" })
                  : t("historical_value", { defaultValue: "Valeur historique" }),
              ];
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            callback: (_value, index) => {
              const item = hourlyData[index];
              if (!item || Number(item.hour) % 3 !== 0) return "";
              const time = moment(item.timestamp)
                .tz(meta?.timezone || "UTC")
                .locale(i18n.language);
              return Number(item.hour) === 0
                ? [time.format("ddd DD/MM"), "00h"]
                : `${String(item.hour).padStart(2, "0")}h`;
            },
            font: { size: 10 },
          },
          grid: {
            color: (context) => {
              const item = hourlyData[context.index];
              return item && Number(item.hour) === 0
                ? "rgba(39, 166, 183, 0.32)"
                : "rgba(0, 0, 0, 0.04)";
            },
          },
        },
        y: { beginAtZero: true, title: { display: true, text: "ETc (mm/h)" } },
      },
    }),
    [hourlyData, i18n.language, meta?.timezone, t],
  );

  const totalEtc = hourlyData.reduce(
    (sum, item) => sum + (Number(item.etc) || 0),
    0,
  );
  const canGoNext = weekStart.getTime() < currentMonday.getTime();

  const selectDate = (value) => {
    if (!value) return;
    const selected = mondayFor(new Date(`${value}T12:00:00`));
    const nextWeek = selected > currentMonday ? currentMonday : selected;
    if (nextWeek.getTime() === weekStart.getTime()) {
      if (noData) showNoDataModal(selectedRange);
      return;
    }
    selectedFromCalendar.current = true;
    setWeekStart(nextWeek);
  };

  const hasVisibleData = displayMode === "daily" ? dailyData.length > 0 : hourlyData.length > 0;

  return (
    <div className="w-100" style={{ backgroundColor: "var(--sf-surface, #ffffff)" }}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 pt-3">
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${displayMode === "daily" ? "btn-info text-white" : "btn-outline-info"}`}
            onClick={() => setDisplayMode("daily")}
            aria-pressed={displayMode === "daily"}
          >
            {t("daily", { defaultValue: "Journalier" })}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${displayMode === "hourly" ? "btn-info text-white" : "btn-outline-info"}`}
            onClick={() => setDisplayMode("hourly")}
            aria-pressed={displayMode === "hourly"}
          >
            {t("hourly", { defaultValue: "Horaire" })}
          </button>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2" style={{ direction: "ltr" }}>
          <div className="btn-group" role="group" aria-label={t("select_week", { defaultValue: "Sélectionner une semaine" })}>
            <button type="button" className="btn btn-sm btn-outline-info" onClick={() => setWeekStart((date) => shiftDate(date, -7))} disabled={loading}>
              ‹ <span className="d-none d-md-inline">{t("previous_week", { defaultValue: "Semaine précédente" })}</span>
            </button>
            <button type="button" className="btn btn-sm btn-outline-info" onClick={() => setWeekStart((date) => shiftDate(date, 7))} disabled={loading || !canGoNext}>
              <span className="d-none d-md-inline">{t("next_week", { defaultValue: "Semaine suivante" })}</span> ›
            </button>
          </div>
          <strong className="text-nowrap">{selectedRange}</strong>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: "150px" }}
            value={formatDateInput(weekStart)}
            max={formatDateInput(new Date())}
            onChange={(event) => selectDate(event.target.value)}
            disabled={loading}
            aria-label={t("select_week", { defaultValue: "Sélectionner une semaine" })}
          />
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 px-3 pt-2 text-muted small">
        <span>{meta?.total_hours || 0}/168 h</span>
        <span>{t("weekly_etc_total", { defaultValue: "ETc totale" })}: {totalEtc.toFixed(2)} mm</span>
        <span>{t("timezone", { defaultValue: "Fuseau" })}: {meta?.timezone || "UTC"}</span>
      </div>

      {!loading &&
        meta?.total_hours > 0 &&
        meta?.hours_with_kc < meta?.total_hours && (
          <div className="alert alert-info mx-3 mt-3 mb-2" role="alert">
            {t("hourly_kc_missing", {
              defaultValue:
                "Le Kc historique n’est pas disponible pour toutes les heures de cette semaine.",
            })}
          </div>
        )}

      {!loading && meta?.warning && (
        <div className="alert alert-warning mx-3 mt-3 mb-2" role="alert">
          {t("hourly_cached_data_warning", { defaultValue: "La météo n'a pas pu être actualisée. Les dernières données enregistrées sont affichées." })}
        </div>
      )}

      {loading && <div className="text-center text-info py-5"><span className="spinner-border spinner-border-sm me-2" role="status" />{t("loading", { defaultValue: "Chargement…" })}</div>}
      {!loading && error && <div className="alert alert-warning mx-3 mt-3 mb-2" role="alert">{error}</div>}
      {!loading && noData && (
        <div className="alert alert-info mx-3 my-4" role="status">
          <strong>{t("etc_no_data_title", { defaultValue: "Aucune donnée ETc disponible" })}</strong>
          <div>{t("etc_no_data_selected_period", { range: selectedRange, defaultValue: "La période {{range}} ne contient aucune donnée d'évapotranspiration (ETc). Choisissez une autre date." })}</div>
        </div>
      )}

      {!loading && !error && !noData && hasVisibleData && displayMode === "daily" && <div className="px-2 pb-3" style={{ height: "330px" }}><EvapoChart data={dailyData} /></div>}
      {!loading && !error && !noData && hasVisibleData && displayMode === "hourly" && (
        <div style={{ overflowX: "auto", width: "100%" }}><div style={{ height: "360px", minWidth: "1800px", padding: "12px 16px" }}><Line data={chart} options={options} /></div></div>
      )}
      {!loading && !fieldUid && fallbackData.length > 0 && <div className="px-2 pb-3" style={{ height: "320px" }}><EvapoChart data={fallbackData} /></div>}
    </div>
  );
};

export default HourlyEvapoChart;
